import { useCallback, useRef, useState, useEffect } from "react";
import { useBulkUpsertFieldValues } from "../api/mutations/field-values/useBulkFieldValues";

export interface FieldValue {
  id?: string;
  field_type_id: string;
  value: any;
  sort_order?: number;
}

export interface FieldError {
  field_type_id: string;
  error: string;
}

export interface UseFieldManagerOptions {
  profile_id: string;
  autoSaveDelay?: number;
  onError?: (errors: FieldError[]) => void;
  onSuccess?: () => void;
  /** Map of fieldTypeId -> fieldValueId for existing values */
  fieldValueIds?: Record<string, string>;
}

export interface UseFieldManagerReturn {
  fields: Record<string, any>;
  setField: (fieldTypeId: string, value: any) => void;
  setFields: (fields: Record<string, any>) => void;

  unsavedFields: Set<string>;
  isUnsaved: boolean;

  fieldErrors: Record<string, string>;
  hasErrors: boolean;
  clearFieldError: (fieldTypeId: string) => void;

  saveFields: () => Promise<void>;
  saveField: (fieldTypeId: string, value: any) => Promise<void>;

  isSaving: boolean;
  isSavingField: (fieldTypeId: string) => boolean;

  resetUnsavedState: () => void;
  revertField: (fieldTypeId: string) => void;
}

export const useFieldManager = ({
  profile_id,
  autoSaveDelay = 5000,
  onError,
  onSuccess,
  fieldValueIds = {},
}: UseFieldManagerOptions): UseFieldManagerReturn => {
  // State management
  const [fields, setFieldsState] = useState<Record<string, any>>({});
  const [originalFields, setOriginalFields] = useState<Record<string, any>>({});
  const [unsavedFields, setunsavedFields] = useState<Set<string>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savingFields, setSavingFields] = useState<Set<string>>(new Set());

  // Refs for debouncing and cleanup
  const autoSaveTimeouts = useRef<Record<string, NodeJS.Timeout>>({});
  const bulkUpsertMutation = useBulkUpsertFieldValues();

  // Computed properties
  const isUnsaved = unsavedFields.size > 0;
  const hasErrors = Object.keys(fieldErrors).length > 0;
  const isSaving = bulkUpsertMutation.isLoading;

  // Individual field save with optimistic update
  const saveField = useCallback(
    async (fieldTypeId: string, value: any) => {
      setSavingFields((prev) => new Set(prev).add(fieldTypeId));

      try {
        // Include existing FieldValue ID if available for proper update
        const fieldValueId = fieldValueIds[fieldTypeId];
        const fieldPayload: FieldValue = { field_type_id: fieldTypeId, value };
        if (fieldValueId) {
          fieldPayload.id = fieldValueId;
        }

        // Optimistic update - field is already updated in UI
        await bulkUpsertMutation.mutateAsync({
          profile_id,
          fields: [fieldPayload],
          replace_mode: false,
        });

        // Update original state to match current state
        setOriginalFields((prev) => ({ ...prev, [fieldTypeId]: value }));
        setunsavedFields((prev) => {
          const newUnsaved = new Set(prev);
          newUnsaved.delete(fieldTypeId);
          return newUnsaved;
        });
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save field";
        setFieldErrors((prev) => ({ ...prev, [fieldTypeId]: errorMessage }));

        onError?.([{ field_type_id: fieldTypeId, error: errorMessage }]);
      } finally {
        setSavingFields((prev) => {
          const newSaving = new Set(prev);
          newSaving.delete(fieldTypeId);
          return newSaving;
        });
      }
    },
    [profile_id, bulkUpsertMutation, onError, fieldValueIds],
  );

  const setField = useCallback(
    (fieldTypeId: string, value: any) => {
      setFieldsState((prev) => {
        const newFields = { ...prev, [fieldTypeId]: value };

        setunsavedFields((prevUnsaved) => {
          const newUnsaved = new Set(prevUnsaved);
          if (originalFields[fieldTypeId] !== value) {
            newUnsaved.add(fieldTypeId);
          } else {
            newUnsaved.delete(fieldTypeId);
          }
          return newUnsaved;
        });

        setFieldErrors((prevErrors) => {
          if (prevErrors[fieldTypeId]) {
            const { [fieldTypeId]: removed, ...rest } = prevErrors;
            return rest;
          }
          return prevErrors;
        });

        return newFields;
      });

      // Auto-save with debounce
      if (autoSaveDelay > 0) {
        if (autoSaveTimeouts.current[fieldTypeId]) {
          clearTimeout(autoSaveTimeouts.current[fieldTypeId]);
        }

        autoSaveTimeouts.current[fieldTypeId] = setTimeout(() => {
          saveField(fieldTypeId, value);
          delete autoSaveTimeouts.current[fieldTypeId];
        }, autoSaveDelay);
      }
    },
    [originalFields, autoSaveDelay, saveField],
  );

  const setFields = useCallback((newFields: Record<string, any>) => {
    setFieldsState(newFields);
    setOriginalFields(newFields);
    setunsavedFields(new Set());
    setFieldErrors({});
  }, []);

  // Bulk save for all unsaved fields
  const saveFields = useCallback(async () => {
    // Clear any pending auto-save timeouts first
    Object.values(autoSaveTimeouts.current).forEach(clearTimeout);
    autoSaveTimeouts.current = {};

    // Find all fields that have changed (including those still in debounce)
    const changedFields: string[] = [];
    Object.keys(fields).forEach((fieldTypeId) => {
      if (originalFields[fieldTypeId] !== fields[fieldTypeId]) {
        changedFields.push(fieldTypeId);
      }
    });

    // Also include explicitly unsaved fields
    unsavedFields.forEach((fieldTypeId) => {
      if (!changedFields.includes(fieldTypeId)) {
        changedFields.push(fieldTypeId);
      }
    });

    if (changedFields.length === 0) return;

    const fieldsToSave = changedFields.map((fieldTypeId) => {
      const field: FieldValue = {
        field_type_id: fieldTypeId,
        value: fields[fieldTypeId],
      };
      // Include existing FieldValue ID if available
      const fieldValueId = fieldValueIds[fieldTypeId];
      if (fieldValueId) {
        field.id = fieldValueId;
      }
      return field;
    });

    try {
      await bulkUpsertMutation.mutateAsync({
        profile_id,
        fields: fieldsToSave,
        replace_mode: false,
      });

      // Update original state to match current state
      const updatedOriginal = { ...originalFields };
      fieldsToSave.forEach((field) => {
        updatedOriginal[field.field_type_id] = field.value;
      });
      setOriginalFields(updatedOriginal);
      setunsavedFields(new Set());
      setFieldErrors({});

      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save fields";

      const fieldErrorsMap: Record<string, string> = {};
      fieldsToSave.forEach((field) => {
        fieldErrorsMap[field.field_type_id] = errorMessage;
      });
      setFieldErrors(fieldErrorsMap);

      const fieldErrors = fieldsToSave.map((field) => ({
        field_type_id: field.field_type_id,
        error: errorMessage,
      }));
      onError?.(fieldErrors);
    }
  }, [
    unsavedFields,
    fields,
    originalFields,
    profile_id,
    bulkUpsertMutation,
    onSuccess,
    onError,
    fieldValueIds,
  ]);

  // Utility functions
  const isSavingField = useCallback(
    (fieldTypeId: string) => {
      return savingFields.has(fieldTypeId);
    },
    [savingFields],
  );

  const clearFieldError = useCallback((fieldTypeId: string) => {
    setFieldErrors((prev) => {
      const { [fieldTypeId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const resetUnsavedState = useCallback(() => {
    setunsavedFields(new Set());
    setOriginalFields({ ...fields });
    setFieldErrors({});

    // Clear any pending timeouts
    Object.values(autoSaveTimeouts.current).forEach(clearTimeout);
    autoSaveTimeouts.current = {};
  }, [fields]);

  const revertField = useCallback(
    (fieldTypeId: string) => {
      const originalValue = originalFields[fieldTypeId];
      setField(fieldTypeId, originalValue);

      // Clear any pending timeout for this field
      if (autoSaveTimeouts.current[fieldTypeId]) {
        clearTimeout(autoSaveTimeouts.current[fieldTypeId]);
        delete autoSaveTimeouts.current[fieldTypeId];
      }
    },
    [originalFields, setField],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(autoSaveTimeouts.current).forEach(clearTimeout);
    };
  }, []);

  return {
    // Field state
    fields,
    setField,
    setFields,

    // Dirty tracking
    unsavedFields,
    isUnsaved,

    // Error handling
    fieldErrors,
    hasErrors,
    clearFieldError,

    // Save operations
    saveFields,
    saveField,

    // Loading states
    isSaving,
    isSavingField,

    // Utilities
    resetUnsavedState,
    revertField,
  };
};
